import { useCallback, useEffect, useRef } from "react";
import api from "../services/api";
import { comparePhotoRevisions, mergeContactPhotoStateSnapshot } from "../services/contactPhoto";

export default function useContactPhotoUpdates({ socket, companyId, onPatch, enabled = true }) {
  const callback = useRef(onPatch);
  callback.current = onPatch;
  const cache = useRef({ companyId, patches: new Map() });
  if (String(cache.current.companyId) !== String(companyId)) cache.current = { companyId, patches: new Map() };

  useEffect(() => {
    if (!enabled || !socket || !companyId) return undefined;
    const channel = `company-${companyId}-contact-photo`;
    let live = true;
    const changed = patch => {
      if (!live || String(cache.current.companyId) !== String(companyId)) return;
      if (!patch || String(patch.companyId) !== String(companyId) || patch.contactId == null || typeof patch.revision !== "string" || !/^\d+$/.test(patch.revision)) return;
      const previous = cache.current.patches.get(String(patch.contactId));
      if (previous && comparePhotoRevisions(patch.revision, previous.revision) <= 0) return;
      cache.current.patches.set(String(patch.contactId), patch);
      if (cache.current.patches.size > 20000) cache.current.patches.delete(cache.current.patches.keys().next().value);
      if (callback.current) callback.current(patch);
    };
    socket.on(channel, changed);
    return () => { live = false; socket.off(channel, changed); };
  }, [socket, companyId, enabled]);

  // Cached events also cover a patch arriving while the first GET is in flight.
  return useCallback((snapshot, current) => mergeContactPhotoStateSnapshot(snapshot, current, cache.current.patches), []);
}

const refreshCooldowns = new Map();
const cooldownMs = 15 * 60 * 1000;

export function useIndividualContactPhoto({ contact, companyId, enabled = true }) {
  const latest = useRef({ contact, companyId, enabled });
  latest.current = { contact, companyId, enabled };
  const refresh = useCallback(reason => {
    const current = latest.current;
    const value = current.contact;
    if (!current.enabled || !value?.id || !current.companyId || (value.companyId != null && String(value.companyId) !== String(current.companyId))) return;
    if (value.isGroup || (value.channel && value.channel !== "whatsapp")) return;
    if (reason === "missing-file" && (!value.urlPicture || /(?:^|\/)nopicture\.png(?:[?#]|$)/.test(value.urlPicture))) return;
    const version = typeof value.photoRevision === "string" ? value.photoRevision : "0";
    const key = `${current.companyId}:${value.id}:${version}`;
    const now = Date.now();
    if ((refreshCooldowns.get(key) || 0) > now) return;
    refreshCooldowns.forEach((until, entry) => { if (until <= now) refreshCooldowns.delete(entry); });
    refreshCooldowns.set(key, now + cooldownMs);
    if (refreshCooldowns.size > 10000) refreshCooldowns.delete(refreshCooldowns.keys().next().value);
    Promise.resolve().then(() => api.post(`/contacts/${value.id}/photo/refresh`, { reason, revision: version }))
      .catch(() => { /* A failed recovery keeps the displayed fallback and cooldown. */ });
  }, []);
  useEffect(() => { refresh("opened"); }, [enabled, companyId, contact?.id, refresh]);
  return useCallback(() => refresh("missing-file"), [refresh]);
}
